import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Shield, Flame } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import StrugglingItemActivitiesDialog from './StrugglingItemActivitiesDialog';

export default function StrugglingPrinciplesDialog({ 
  open, 
  onClose, 
  principlesStats, 
  immutables,
  progressReports,
  activities,
  officers 
}) {
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemIsRedLine, setSelectedItemIsRedLine] = useState(false);

  const { strugglingCounts, totalCounts } = principlesStats;

  // Sort by total count descending
  const sortedItems = Object.keys(totalCounts).map(item => ({
    item,
    struggling: strugglingCounts[item] || 0,
    total: totalCounts[item] || 0
  })).sort((a, b) => b.total - a.total);

  const maxCount = sortedItems.length > 0 ? sortedItems[0].total : 1;

  // Determine if item is a red line
  const isRedLine = (item) => {
    return immutables.some(imm => 
      imm.statement === item && 
      ['company_red_line', 'people_red_line'].includes(imm.category)
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="dash-strugglingprinciplesdialog-dialog" className="bg-slate-900 text-white border-slate-800 max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Shield className="w-6 h-6 text-indigo-400" />
            System-Wide Struggling Values & Principles
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-4">
          {sortedItems.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              No items reported across the system
            </div>
          ) : (
            sortedItems.map(({ item, struggling, total }) => {
              const isRL = isRedLine(item);
              const strugglingPercent = (struggling / maxCount) * 100;
              const nonStrugglingPercent = ((total - struggling) / maxCount) * 100;

              return (
                <div 
                  key={item} 
                  className="space-y-1 cursor-pointer hover:bg-slate-800/50 p-2 rounded-lg transition-colors"
                  onClick={() => {
                    setSelectedItem(item);
                    setSelectedItemIsRedLine(isRL);
                  }}
                >
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 flex-1">
                      {isRL ? (
                        <Flame className="w-4 h-4 text-rose-400 flex-shrink-0" />
                      ) : (
                        <Shield className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                      )}
                      <span className="text-slate-300 line-clamp-2">{item}</span>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <Badge className="bg-red-500/20 text-red-400 border-none">
                        {struggling}
                      </Badge>
                      <span className="text-slate-500 text-xs">/</span>
                      <Badge variant="outline" className="border-slate-700 text-slate-400">
                        {total}
                      </Badge>
                    </div>
                  </div>
                  <div className="h-8 bg-slate-800 rounded-lg overflow-hidden flex">
                    <div 
                      className="h-full bg-red-500 flex items-center justify-center text-white font-semibold text-sm transition-all hover:bg-red-600"
                      style={{ width: `${strugglingPercent}%`, minWidth: struggling > 0 ? '30px' : '0' }}
                    >
                      {struggling > 0 ? struggling : ''}
                    </div>
                    <div 
                      className="h-full bg-indigo-500 flex items-center justify-center text-white font-semibold text-sm transition-all hover:bg-indigo-600"
                      style={{ width: `${nonStrugglingPercent}%`, minWidth: (total - struggling) > 0 ? '30px' : '0' }}
                    >
                      {(total - struggling) > 0 ? (total - struggling) : ''}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <StrugglingItemActivitiesDialog
          open={!!selectedItem}
          onClose={() => setSelectedItem(null)}
          item={selectedItem}
          progressReports={progressReports}
          activities={activities}
          officers={officers}
          isRedLine={selectedItemIsRedLine}
        />
      </DialogContent>
    </Dialog>
  );
}